import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  SiteSettings,
  SocialLink,
  MenuData,
  Faq,
  PaymentMethod,
  AboutPageData,
  ContactInfoData,
  HomepageSection,
  Branch,
  GlobalFrame,
  GlobalMaterial,
  GlobalSize,
  PublicCategory,
  ShippingZone,
} from '../types';

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<SiteSettings>('/settings')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMenu(location: 'header' | 'footer') {
  return useQuery({
    queryKey: ['menus', location],
    queryFn: async () => (await api.get<MenuData>(`/menus/${location}`)).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSocialLinks() {
  return useQuery({
    queryKey: ['social-links'],
    queryFn: async () => (await api.get<SocialLink[]>('/social-links')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => (await api.get<PaymentMethod[]>('/payment-methods')).data,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Page-level hooks — not layout-critical, no localStorage needed
// ---------------------------------------------------------------------------

export function useFaqs() {
  return useQuery({
    queryKey: ['faqs'],
    queryFn: async () => (await api.get<Faq[]>('/faqs')).data,
  });
}

export function useAboutPage() {
  return useQuery({
    queryKey: ['about-page'],
    queryFn: async () => (await api.get<AboutPageData>('/about-page')).data,
  });
}

export function useContactInfo() {
  return useQuery({
    queryKey: ['contact-info'],
    queryFn: async () => (await api.get<ContactInfoData>('/contact-info')).data,
  });
}

export function usePublicCategories() {
  return useQuery({
    queryKey: ['public-categories'],
    queryFn: async () => (await api.get<PublicCategory[]>('/categories')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useHomepageSections() {
  return useQuery({
    queryKey: ['homepage-sections'],
    queryFn: async () => (await api.get<HomepageSection[]>('/homepage-sections')).data,
    refetchOnWindowFocus: true,
  });
}

export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await api.get<Branch[]>('/branches')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGlobalFrames() {
  return useQuery({
    queryKey: ['global-frames'],
    queryFn: async () => (await api.get<GlobalFrame[]>('/frames')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGlobalMaterials() {
  return useQuery({
    queryKey: ['global-materials'],
    queryFn: async () => (await api.get<GlobalMaterial[]>('/materials')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGlobalSizes() {
  return useQuery({
    queryKey: ['global-sizes'],
    queryFn: async () => (await api.get<GlobalSize[]>('/sizes')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useShippingZones() {
  return useQuery({
    queryKey: ['shipping-areas'],
    queryFn: async () => (await api.get<ShippingZone[]>('/shipping-areas')).data,
    staleTime: 2 * 60 * 1000,
  });
}

export interface PublicReview {
  id: number;
  customer_name: string;
  title: string | null;
  customer_photo: string | null;
  customer_country: string | null;
  company: string | null;
  rating: number;
  comment: string | null;
  images: string[];
  verified_purchase: boolean;
  featured: boolean;
  review_date: string | null;
  product: { name: string; slug: string } | null;
}

export function useReviews(limit = 12) {
  return useQuery({
    queryKey: ['reviews', limit],
    queryFn: async () => (await api.get<PublicReview[]>('/reviews', { params: { limit } })).data,
    staleTime: 5 * 60 * 1000,
  });
}

export interface SizeGuideData {
  image_path: string | null;
}

export function useSizeGuide() {
  return useQuery({
    queryKey: ['size-guide'],
    queryFn: async () => (await api.get<SizeGuideData>('/size-guide')).data,
    staleTime: 5 * 60 * 1000,
  });
}
